import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import {
  HospitalSearchMode,
  HospitalSearchParamsQueries,
  HospitalSearchSortType,
} from '@/types/hospital';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { faceTofaceLogger } from '@/utils/logger';
import { deleteQueryString, updateSearchParams } from '@/utils/url';
import { validateNonSpecialPattern } from '@/utils/validate';

const useSearchHospitalsByKeyword = () => {
  const queryClient = useQueryClient();
  const { router, pageName } = useCustomRouter();
  const { showModal, closeModal } = useModal();

  const { keyword: queryKeyword = '' } = router.query as {
    keyword?: string;
  };

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>(queryKeyword);
  const [keyword, setKeyword] = useState<string>(queryKeyword);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleResetInput = async (e: React.MouseEvent) => {
    e.preventDefault();

    setInputValue('');
    setKeyword('');
    searchInputRef.current?.blur();
    deleteQueryString(HospitalSearchParamsQueries.Keyword);
    await queryClient.invalidateQueries(['SearchHospitalGenerally']);
  };

  const handleChangeKeyword = async () => {
    const inputKeyword = inputValue.trim();
    if (!inputKeyword) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '검색어를 입력해주세요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.BOLD,
              label: '확인',
              onClick: () => closeModal(),
            },
          ],
        },
      });

      return;
    }

    if (inputKeyword.length < 2) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '2글자 이상 입력해 주세요.',
          message: '더 정확도 높은 결과를 볼 수 있어요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.BOLD,
              label: '확인',
              onClick: () => closeModal(),
            },
          ],
        },
      });

      return;
    }
    if (!validateNonSpecialPattern(inputKeyword)) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '특수문자를 지원하지 않아요.',
          message: '다른 검색어를 입력해주세요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.BOLD,
              label: '확인',
              onClick: () => closeModal(),
            },
          ],
        },
      });

      return;
    }

    faceTofaceLogger({
      eventName: `click_search`,
      pageName: pageName,
      payload: {
        section: 'search_bar',
        keyword: inputKeyword,
      },
    });

    updateSearchParams([
      { key: HospitalSearchParamsQueries.Keyword, value: inputKeyword },
      {
        key: HospitalSearchParamsQueries.Mode,
        value: HospitalSearchMode.SEARCH,
      },
      {
        key: HospitalSearchParamsQueries.SortType,
        value: HospitalSearchSortType.ACCURACY,
      },
    ]);

    setSearchKeyword(inputKeyword);
  };

  const setSearchKeyword = (keyword: string) => {
    setKeyword(keyword);
    searchInputRef.current?.blur();
  };

  return {
    inputValue,
    searchInputRef,
    isVisibleDeleteIcon: !!keyword,
    handleChangeKeyword,
    handleInputChange,
    handleResetInput,
    keyword,
  };
};

export default useSearchHospitalsByKeyword;
